import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import PublicNavbar from "../components/PublicNavbar";
import Footer from "../components/Footer";
import { openInteractiveDemo } from "../components/demo/DemoLauncher";
import { SOLUTION_PAGES } from "../data/solutionPages";
import { ArrowRight, Gift, Play, BookOpen } from "lucide-react";

const OVERLINE = "text-xs font-bold tracking-[0.2em] uppercase text-violet-600";

export default function SolutionsIndex() {
  useEffect(() => {
    document.title = "Solutions Junago : répondre aux vrais problèmes des loueurs de voitures";
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <PublicNavbar />

      {/* Hero */}
      <section className="max-w-6xl mx-auto px-6 pt-14 md:pt-20 pb-14" data-testid="solutions-hero">
        <div className={`${OVERLINE} mb-4`}>Solutions</div>
        <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tight leading-[1.05] text-slate-900 max-w-3xl" data-testid="solutions-title">
          Vous ne cherchez pas un logiciel. Vous cherchez à résoudre un problème.
        </h1>
        <p className="mt-6 text-base md:text-lg text-slate-600 leading-relaxed max-w-2xl">
          Perte de temps, réservations dispersées, impayés, manque de visibilité : chaque solution Junago
          part d'un problème métier réel — et montre comment plusieurs modules travaillent ensemble pour le résoudre.
        </p>
        <div className="mt-8 flex flex-col sm:flex-row gap-3">
          <Link to="/register?context=vendor" className="btn-primary inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full text-sm font-semibold" data-testid="solutions-cta-start">
            <Gift className="h-4 w-4" /> Commencer gratuitement
          </Link>
          <button type="button" onClick={openInteractiveDemo} className="btn-jn-secondary inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full text-sm font-semibold" data-testid="solutions-cta-demo">
            <Play className="h-4 w-4" /> Voir la démo interactive
          </button>
        </div>
      </section>

      {/* Cartes solutions */}
      <section className="bg-slate-50 border-y border-slate-200" data-testid="solutions-grid">
        <div className="max-w-6xl mx-auto px-6 py-16 md:py-20">
          <div className="grid md:grid-cols-2 gap-5">
            {SOLUTION_PAGES.map((s, i) => (
              <Link
                key={s.slug}
                to={`/solutions/${s.slug}`}
                className="group rounded-2xl border border-slate-200 bg-white p-8 hover:border-violet-300 hover:shadow-[0_16px_40px_-16px_rgba(124,58,237,0.3)] transition-all"
                data-testid={`solutions-card-${s.slug}`}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="h-11 w-11 rounded-xl bg-violet-50 flex items-center justify-center">
                    <s.icon className="h-5 w-5 text-violet-600" />
                  </div>
                  <span className="text-[11px] font-bold text-slate-300">0{i + 1}</span>
                </div>
                <div className="mt-4 text-[11px] font-bold tracking-[0.15em] uppercase text-violet-600">{s.menuLabel}</div>
                <h2 className="mt-2 font-heading text-xl font-medium text-slate-900 tracking-tight leading-snug group-hover:text-violet-700 transition-colors">
                  {s.title}
                </h2>
                <p className="mt-3 text-sm text-slate-600 leading-relaxed line-clamp-3">{s.problem.intro}</p>
                <span className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-violet-600">
                  Découvrir la solution <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-1" />
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Lien cas d'usage */}
      <section className="max-w-6xl mx-auto px-6 py-14" data-testid="solutions-use-cases-link">
        <div className="rounded-3xl border border-slate-200 p-8 md:p-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div>
            <div className={`${OVERLINE} mb-2`}>Junago en situation</div>
            <h2 className="font-heading text-xl md:text-2xl font-medium tracking-tight text-slate-900">Découvrez comment ces solutions s'appliquent au quotidien d'un loueur</h2>
          </div>
          <Link to="/cas-d-usage" className="btn-jn-secondary inline-flex items-center gap-2 px-6 py-3 rounded-full text-sm font-semibold shrink-0" data-testid="solutions-to-use-cases">
            <BookOpen className="h-4 w-4" /> Voir les cas d'usage
          </Link>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-5xl mx-auto px-6 pb-16 md:pb-20" data-testid="solutions-cta-bottom">
        <div className="relative overflow-hidden rounded-3xl bg-slate-950 text-white px-8 sm:px-14 py-14 text-center">
          <div aria-hidden className="absolute -right-20 -bottom-32 h-96 w-96 rounded-full bg-violet-600 opacity-25 blur-3xl" />
          <div className="relative">
            <h2 className="font-heading text-3xl md:text-4xl font-medium tracking-tight">Votre problème n'est pas dans la liste ?</h2>
            <p className="mt-3 text-slate-400">Essayez Junago 30 jours ou explorez toutes les fonctionnalités en détail.</p>
            <div className="mt-7 flex flex-col sm:flex-row gap-3 justify-center">
              <Link to="/register?context=vendor" className="btn-jn-light inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full text-sm font-semibold" data-testid="solutions-bottom-start">
                Commencer gratuitement <ArrowRight className="btn-arrow h-4 w-4" />
              </Link>
              <Link to="/fonctionnalites" className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full text-sm font-semibold border border-white/20 text-white hover:bg-white/10 transition-colors" data-testid="solutions-bottom-features">
                Toutes les fonctionnalités
              </Link>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
